import bundledPolicy from "../../policy/policy.json";
import { Policy } from "../models/policy";

// Injected by webpack DefinePlugin (see webpack.config.js); event-runtime fetches require
// an absolute URL since there's no DOM location to resolve a relative one against.
declare const ADDIN_BASE_URL: string;

const POLICY_URL = `${ADDIN_BASE_URL}/policy/policy.json`;
const FETCH_TIMEOUT_MS = 1500;

/**
 * Validates the rules from §8.3: threshold range, lower-case/no-@ domains appearing in only
 * one list, unique customer group ids, and a cancelLabel within 20 characters.
 */
export function isValidPolicy(candidate: unknown): candidate is Policy {
  if (typeof candidate !== "object" || candidate === null) {
    return false;
  }
  const policy = candidate as Partial<Policy>;

  if (policy.schemaVersion !== 1) return false;
  if (typeof policy.policyVersion !== "string" || policy.policyVersion.length === 0) return false;
  if (typeof policy.threshold !== "number" || policy.threshold < 2 || policy.threshold > 10) return false;
  if (!Array.isArray(policy.internalDomains) || !Array.isArray(policy.ignoredDomains)) return false;
  if (!Array.isArray(policy.customerGroups)) return false;

  const labelRule = policy.labelRule;
  if (!labelRule || typeof labelRule.enabled !== "boolean" || !Array.isArray(labelRule.blockedLabelNames)) return false;
  if (typeof labelRule.blockUnlabeled !== "boolean") return false;

  const { lookalikeRule, brandDomainRule, contentRule, externalLimitRule } = policy;
  if (!lookalikeRule || typeof lookalikeRule.enabled !== "boolean") return false;
  if (!brandDomainRule || typeof brandDomainRule.enabled !== "boolean") return false;

  const customerLabelRule = policy.customerLabelRule;
  if (!customerLabelRule || typeof customerLabelRule.enabled !== "boolean") return false;
  if (customerLabelRule.scope !== "otherCustomers" && customerLabelRule.scope !== "anyOtherExternal") return false;
  if (!contentRule || typeof contentRule.enabled !== "boolean") return false;
  if (typeof contentRule.maxBodyChars !== "number" || contentRule.maxBodyChars < 100 || contentRule.maxBodyChars > 20000) {
    return false;
  }
  if (!externalLimitRule || typeof externalLimitRule.enabled !== "boolean") return false;
  if (typeof externalLimitRule.max !== "number" || externalLimitRule.max < 2 || externalLimitRule.max > 100) return false;
  if (!labelRule.blockedLabelNames.every((n) => typeof n === "string" && n.trim().length > 0)) return false;

  const isLowerCaseNoAt = (d: unknown): d is string =>
    typeof d === "string" && d.length > 0 && d === d.toLowerCase() && !d.includes("@");

  if (!policy.internalDomains.every(isLowerCaseNoAt)) return false;
  if (!policy.ignoredDomains.every(isLowerCaseNoAt)) return false;

  const seenDomains = new Set<string>();
  for (const d of [...policy.internalDomains, ...policy.ignoredDomains]) {
    if (seenDomains.has(d)) return false; // a domain must appear in only one list
    seenDomains.add(d);
  }

  const seenGroupIds = new Set<string>();
  for (const group of policy.customerGroups) {
    if (typeof group.id !== "string" || typeof group.name !== "string" || !Array.isArray(group.domains)) {
      return false;
    }
    if (seenGroupIds.has(group.id)) return false; // customer group ids must be unique
    seenGroupIds.add(group.id);
    if (!group.domains.every(isLowerCaseNoAt)) return false;
    if (group.keywords !== undefined) {
      if (!Array.isArray(group.keywords)) return false;
      if (!group.keywords.every((k) => typeof k === "string" && k.trim().length > 0)) return false;
    }
    if (group.labelNames !== undefined) {
      if (!Array.isArray(group.labelNames)) return false;
      if (!group.labelNames.every((n) => typeof n === "string" && n.trim().length > 0)) return false;
    }
    for (const d of group.domains) {
      if (seenDomains.has(d)) return false;
      seenDomains.add(d);
    }
  }

  const messages = policy.messages as Policy["messages"] | undefined;
  if (!messages || !messages.es || !messages.en || !messages.ja) return false;
  for (const set of [messages.es, messages.en, messages.ja]) {
    if (typeof set.crossCustomer !== "string" || set.crossCustomer.length === 0) return false;
    if (typeof set.standard !== "string" || set.standard.length === 0) return false;
    if (typeof set.labelBlocked !== "string" || set.labelBlocked.length === 0) return false;
    if (typeof set.unlabeledBlocked !== "string" || set.unlabeledBlocked.length === 0) return false;
    if (typeof set.lookalikeDomain !== "string" || set.lookalikeDomain.length === 0) return false;
    if (typeof set.brandInDomain !== "string" || set.brandInDomain.length === 0) return false;
    if (typeof set.customerLabelBlocked !== "string" || set.customerLabelBlocked.length === 0) return false;
    // leaves room for the recipient list within Outlook's 500 character dialog limit
    if (Object.entries(set).some(([key, text]) => key !== "cancelLabel" && String(text).length > 400)) return false;
    if (typeof set.contentMismatch !== "string" || set.contentMismatch.length === 0) return false;
    if (typeof set.tooManyExternal !== "string" || set.tooManyExternal.length === 0) return false;
    if (typeof set.cancelLabel !== "string" || set.cancelLabel.length === 0 || set.cancelLabel.length > 20) {
      return false;
    }
  }

  if (!policy.telemetry || typeof policy.telemetry.enabled !== "boolean") return false;

  return true;
}

function getBundledPolicy(): Policy {
  return bundledPolicy as Policy;
}

/**
 * Fetches policy.json with a 1.5s timeout and validates it; falls back to the bundled copy
 * on any timeout, network error or schema violation (FR-16/FR-17).
 */
export async function loadPolicy(fetchImpl: typeof fetch = fetch): Promise<Policy> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetchImpl(POLICY_URL, { cache: "no-store", signal: controller.signal });
      if (!response.ok) {
        return getBundledPolicy();
      }
      const data = await response.json();
      return isValidPolicy(data) ? data : getBundledPolicy();
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return getBundledPolicy();
  }
}
